import getJsonLdScriptId from "@insite/client-framework/Common/Utilities/getJsonLdScriptId";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import setPageMetadata, { Metadata } from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import { HasDeferredApiContext, withCallDeferredApi } from "@insite/client-framework/Components/DeferredApiContext";
import { ParentProductIdContext } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import prepareProduct from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/PrepareProduct";
import {
    getSelectedCategoryPath,
    getSelectedProductPath,
    getSession,
    getSettingsCollection,
    getShouldPreventCatalogLoop,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getProductState,
    getProductStateByPath,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import changeQtyOrdered from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeQtyOrdered";
import changeUnitOfMeasure from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeUnitOfMeasure";
// import generateStructuredPageData from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import loadProductPricingAndInventory from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/LoadProductPricingAndInventory";
import resetSelections from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ResetSelections";
import setMetadataSetForId from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetMetadataSetForId";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { HideFooter, HideHeader } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CurrentCategory from "@insite/content-library/Components/CurrentCategory";
import Modals from "@insite/content-library/Components/Modals";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { AdminCustomSettings } from "@tCooneyBrothers/ApiModels";
import SeoProductDetail from "@tCooneyBrothers/Components/SeoProductDetail";
import loadCustomRelatedProducts from "@tCooneyBrothers/Handlers/LoadCustomRelatedProducts";
import generateStructuredPageData from "@tCooneyBrothers/Overrides/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import { getComputedVariantProduct } from "@tCooneyBrothers/Store/Data/Products/ProductsSelectorsExtension";
import displayProduct from "@tCooneyBrothers/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsStyles {
    mainContainer?: InjectableCss;
}

const styles: ProductDetailsStyles = {
    mainContainer: {
        css: css`
            [class^="GridWrapper"] {
                &:nth-of-type(2) {
                    margin: 10px 0;
                    [class^="GridItemStyle"] {
                        align-items: center;
                        &:last-of-type {
                            [data-test-selector="productDetails_htmlContent"] {
                                margin-bottom: 10px;

                                &:last-of-type {
                                    margin-bottom: 0;
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let productPath =
        getSelectedProductPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ productPath: string }>(location.search).productPath
            : "") ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    let queryString = location.search;
    if (location.pathname.toLowerCase().startsWith("/content/page/") && productPath.indexOf("?") > -1) {
        queryString = productPath ? productPath.substring(productPath.indexOf("?")) : "";
        productPath = productPath.substring(0, productPath.indexOf("?"));
    }
    const productState = getProductStateByPath(state, productPath);
    const catalogPageState = getCatalogPageStateByPath(state, productPath);
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
    const variantChildren = getVariantChildrenDataView(state, productState.id);
    const settingsCollection = getSettingsCollection(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        productState: computedVariantProduct,
        variantProductState,
        catalogPageState,
        parentProductState: productState,
        productInfo:
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined,
        productPath,
        queryString,
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        lastProductPath: state.pages.productDetails.lastProductPath,
        lastStyledOption: state.pages.productDetails.lastStyledOption,
        websiteName: state.context.website.name,
        page: getCurrentPage(state),
        location,
        websiteSettings: settingsCollection.websiteSettings,
        searchPath: settingsCollection.searchSettings.searchPath,
        selectedProductId: state.pages.productDetails.selectedProductId,
        isProductLoading: state.pages.productDetails.isProductLoading,
        metadataSetForId: state.pages.productDetails.metadataSetForId,
        structuredPageData: state.pages.productDetails.structuredPageData,
        structuredPageDataSetForId: state.pages.productDetails.structuredPageDataSetForId,
        session: getSession(state),
        productInfosById: state.pages.productDetails.productInfosById,
        variantChildren,
        enableServerSideRendering: settingsCollection.websiteSettings.enableServerSideRendering,
        adminCustomSettings: getSettingsCollection(state).adminCustomSettings as AdminCustomSettings,
        realTimePricingAndQty: state.pages.productDetails.productInfosById,
    };
};

const mapDispatchToProps = {
    displayProduct,
    changeQtyOrdered,
    changeUnitOfMeasure,
    resetSelections,
    generateStructuredPageData,
    setMetadataSetForId,
    loadProductPricingAndInventory,
    prepareProduct,
    loadCustomRelatedProducts,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    PageProps &
    HasDeferredApiContext;

class ProductDetailsPage extends React.Component<Props> {
    timeoutId: ReturnType<typeof setTimeout> | null = null; // Allow timeoutId to be a number or null
    // componentDidMount(): void {
    //     this.trySetMetadata();
    // }
    UNSAFE_componentWillMount() {
        const {
            deferredApiCalls,
            callDeferredApis,
            loadProductPricingAndInventory,
            productState,
            productInfosById,
            variantChildren,
        } = this.props;

        if (!getShouldPreventCatalogLoop(deferredApiCalls)) {
            this.displayProductIfNeeded();
        }
        callDeferredApis({
            pricingHandler: () => {
                loadProductPricingAndInventory({
                    product: productState.value,
                    productInfosById,
                    variantChildren: variantChildren.value,
                    styledOption: this.getStyledOption(),
                });
            },
            inventoryHandler: () => {
                loadProductPricingAndInventory({
                    product: productState.value,
                    productInfosById,
                    variantChildren: variantChildren.value,
                    styledOption: this.getStyledOption(),
                });
            },
        });

        // Set title/meta during server render so raw HTML includes SEO tags.
        this.trySetMetadata();
    }

    componentDidMount(): void {
        const { enableServerSideRendering, prepareProduct, productState, loadCustomRelatedProducts } = this.props;
        if (enableServerSideRendering) {
            const {
                productState: { value: product },
            } = this.props;

            if (!product) {
                return;
            }

            prepareProduct({ product });
        }

        const productId = productState.value?.id;
        if (productId) {
            loadCustomRelatedProducts({
                productId,
                relatedProductType: "Accessory",
            });
        }
    }

    componentDidUpdate(prevProps: Props): void {
        const { metadataSetForId, productState, loadCustomRelatedProducts, realTimePricingAndQty } = this.props;
        if (prevProps !== this.props) {
            this.trySetMetadata();
        }
        this.displayProductIfNeeded();
        const prevId = prevProps.productState.value?.id;
        const currentId = productState.value?.id;

        if (currentId && prevId !== currentId) {
            loadCustomRelatedProducts({
                productId: currentId,
                relatedProductType: "Accessory",
            });
        }

        if (productState.value && productState.value.id !== metadataSetForId && this.trySetMetadata()) {
            trackPageChange();
            // this.addPriceAndQuantity();
        }
        if (prevProps.realTimePricingAndQty !== realTimePricingAndQty) {
            const productID = productState.value?.id ?? "";
            const prevQuantity = prevProps.realTimePricingAndQty?.[productID]?.["qtyOrdered"];
            const updatedQuantity = realTimePricingAndQty?.[productID]?.["qtyOrdered"];
            if (prevQuantity === updatedQuantity) {
                this.addPriceAndQuantity();
            }
        }
    }

    componentWillUnmount() {
        const { resetSelections } = this.props;
        resetSelections();
    }

    addPriceAndQuantity() {
        const { productState, realTimePricingAndQty } = this.props;
        const productID = productState.value?.id;
        if (productID) {
            const PriceInfo = realTimePricingAndQty?.[productID]?.["pricing"]?.["unitNetPrice"];
            const QtyInfo = realTimePricingAndQty?.[productID]?.["inventory"]?.["qtyOnHand"];
            if (PriceInfo !== undefined && PriceInfo !== null && QtyInfo !== undefined && QtyInfo !== null) {
                this.addToDataLayer(productState.value, PriceInfo, QtyInfo);
            }
        }
    }

    addToDataLayer = (
        product: Readonly<ProductModel> | undefined,
        priceInfo: number | undefined,
        qtyInfo: number | undefined,
    ) => {
        const categoryArr = this.props.catalogPageState.id?.split("/") || [];
        // const categoryArr1 = this.props.catalogPageState.value?.breadCrumbs || [];
        // const categories1 = categoryArr1.slice(4, categoryArr1?.length - 1).map(cat => cat.text);
        const categories = categoryArr.slice(1, categoryArr.length);
        const category =
            categories.length === 0
                ? "Search"
                : (this.props.category?.shortDescription ?? product?.properties.categoryTitle);
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "view_item",
                ecommerce: {
                    items: [
                        {
                            item_id: product?.productNumber || "",
                            item_name: product?.productTitle || "",
                            index: 0,
                            item_brand: product?.brand?.name || "",
                            item_category: category || product?.urlSegment,
                            price: priceInfo,
                            quantity: qtyInfo,
                            item_list_id: product?.id,
                            item_list_name: product?.properties.categoryTitle || category || product?.urlSegment,
                        },
                    ],
                },
            });
        }
    };

    trySetMetadata() {
        const {
            productState: { value: product },
            catalogPageState,
            websiteName,
            location,
            websiteSettings,
            page,
            structuredPageData,
            productInfo,
            session,
            metadataSetForId,
            structuredPageDataSetForId,
            generateStructuredPageData,
            selectedProductId,
            variantProductState,
            setMetadataSetForId,
            adminCustomSettings,
        } = this.props;
        if (!catalogPageState?.value || !product) {
            return false;
        }

        const {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            title,
            alternateLanguageUrls,
        } = catalogPageState.value;

        const altPageTitle = `${product.brand?.name} ${product.manufacturerItem}`;
        const altMetaDescription = product.productTitle;
        let useMetaKeywords = metaKeywords;

        if (adminCustomSettings && !adminCustomSettings.useMetaKeywordsOnExternalWebsite) {
            useMetaKeywords = "";
        }

        const pageMetaData: Metadata = {
            metaDescription: metaDescription || altMetaDescription,
            metaKeywords: useMetaKeywords,
            openGraphImage: product.mediumImagePath,
            // openGraphImage:
            //     product.mediumImagePath || openGraphImage || product.largeImagePath || product.smallImagePath,
            openGraphTitle,
            openGraphUrl,
            currentPath: location.pathname,
            canonicalPath: product.canonicalUrl,
            title: title || altPageTitle || product.productTitle,
            websiteName,
            alternateLanguageUrls: alternateLanguageUrls ?? undefined,
            structuredPageData,
            jsonLdScriptId: getJsonLdScriptId(page.type),
        };

        if (catalogPageState.value.productId !== selectedProductId) {
            const variantContent = variantProductState.value?.content;
            if (variantContent) {
                const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
                const urlAddress = `${url.protocol.toLowerCase()}//${url.host.toLowerCase()}`;
                // const imagePath =
                //     variantContent.openGraphImage ||
                //     product.smallImagePath ||
                //     product.mediumImagePath ||
                //     product.largeImagePath;

                const imagePath = product.mediumImagePath || variantContent.openGraphImage;

                const openGraphImage =
                    !imagePath || imagePath.indexOf("http://") > -1 || imagePath.indexOf("https://") > -1
                        ? imagePath
                        : urlAddress + imagePath.startsWith("/")
                          ? imagePath
                          : `/${imagePath}`;
                let openGraphUrl = variantContent.openGraphUrl || product.canonicalUrl;

                openGraphUrl =
                    !openGraphUrl || openGraphUrl.indexOf("http://") > -1 || openGraphUrl.indexOf("https://") > -1
                        ? openGraphUrl
                        : urlAddress + openGraphUrl.startsWith("/")
                          ? openGraphUrl
                          : `/${openGraphUrl}`;
                const pageTitle = variantContent.pageTitle || product.productTitle;

                pageMetaData.title = pageTitle;
                pageMetaData.metaKeywords =
                    variantContent.metaKeywords || `${product.productTitle} ${product.productNumber}`;
                pageMetaData.metaDescription = variantContent.metaDescription || product.productTitle;
                pageMetaData.openGraphTitle = variantContent.openGraphTitle || pageTitle;
                pageMetaData.openGraphImage = openGraphImage || "";
                pageMetaData.openGraphUrl = openGraphUrl || "";
            }
        }

        if (!openGraphImage && !pageMetaData.openGraphImage && product.images && product.images?.length > 0) {
            pageMetaData.openGraphImage = product.images[0].smallImagePath || product.images[0].mediumImagePath;
        }

        setPageMetadata(pageMetaData, websiteSettings);

        if (websiteSettings.enableProductStructuredData && product.id !== structuredPageDataSetForId) {
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }

            this.timeoutId = setTimeout(() => {
                generateStructuredPageData({
                    product,
                    productInfo,
                    session,
                    brandId: catalogPageState.value.brandId || product.brand?.id,
                    categoryIdWithBrandId: catalogPageState.value.categoryIdWithBrandId,
                    categoryId: catalogPageState.value.categoryId,
                });

                this.timeoutId = null;
            }, 5000);

            return false;
        }

        if (product.id !== metadataSetForId) {
            setMetadataSetForId({
                metadataSetForId: product.id,
            });
        }
        return true;
    }

    getStyledOption() {
        const { queryString } = this.props;
        const queryParams = parseQueryString<{ option?: string; criteria?: string }>(queryString.replace("?", ""));
        return (queryParams.option?.toString() || queryParams.criteria?.toString() || "").toLocaleLowerCase();
    }

    displayProductIfNeeded() {
        const {
            productPath,
            queryString,
            lastProductPath,
            lastStyledOption,
            isProductLoading,
            searchPath,
            displayProduct,
        } = this.props;
        const styledOption = this.getStyledOption();

        if (isProductLoading) {
            return;
        }

        if (
            (productPath.toLowerCase() === lastProductPath?.toLowerCase() &&
                styledOption === lastStyledOption?.toLowerCase()) ||
            productPath.toLowerCase() === `/${searchPath.toLowerCase()}`
        ) {
            return;
        }

        displayProduct({ path: productPath, styledOption });
    }

    render() {
        const {
            productState,
            productInfo,
            productPath,
            lastProductPath,
            changeQtyOrdered,
            changeUnitOfMeasure,
            selectedProductId,
            parentProductState,
            id,
        } = this.props;
        if (
            !productState.value ||
            productState.isLoading ||
            !productInfo ||
            (productPath && lastProductPath && productPath.toLowerCase() !== lastProductPath.toLowerCase())
        ) {
            return null;
        }
        const productId = productState.value.id;

        const productContext: ProductContextModel = {
            product: productState.value,
            productInfo,
            onQtyOrderedChanged: o => {
                changeQtyOrdered({
                    qtyOrdered: o,
                    productId,
                });
            },
            onUnitOfMeasureChanged: o => {
                changeUnitOfMeasure({
                    unitOfMeasure: o,
                    productId,
                });
            },
        };

        return (
            <Page data-test-selector={`productDetails_productId_${productId}`} {...styles.mainContainer}>
                <SeoProductDetail productContext={productContext}></SeoProductDetail>
                {selectedProductId && (
                    <CurrentCategory>
                        <ProductContext.Provider value={productContext}>
                            <ParentProductIdContext.Provider value={parentProductState?.value?.id}>
                                <Zone contentId={id} zoneName="Content" />
                            </ParentProductIdContext.Provider>
                        </ProductContext.Provider>
                    </CurrentCategory>
                )}
                <Modals />
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCallDeferredApi(ProductDetailsPage)),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: false,
        supportsProductSelection: true,
        pageType: "System",
        fieldDefinitions: [HideHeader, HideFooter],
    },
};

export default pageModule;

export const ProductDetailsPageContext = "ProductDetailsPage";
